import { useRef, useState } from "react";
import { Loader2, Sparkles, Trash2, Upload } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const GST_SLABS = [0, 5, 12, 18, 28];

export function BulkProductIngest({ open, onOpenChange, categories = [], onSaved }) {
  const fileRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [failed, setFailed] = useState([]);

  const scan = async (files) => {
    if (!files?.length) return;
    setScanning(true);
    setFailed([]);
    try {
      const body = new FormData();
      Array.from(files).slice(0, 10).forEach((f) => body.append("files", f));
      const { data } = await api.post("/products/ai-scan", body,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      setRows((r) => [...r, ...data.suggestions.map((s, i) => ({ ...s, uid: `${Date.now()}-${i}` }))]);
      setFailed(data.failed || []);
      toast.success(`${data.suggestions.length} product(s) read from your photos`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setRow = (uid, patch) =>
    setRows((r) => r.map((x) => (x.uid === uid ? { ...x, ...patch } : x)));

  const saveAll = async () => {
    const products = rows
      .filter((r) => r.name.trim())
      .map(({ uid, filename, confidence, ...p }) => ({
        ...p,
        standard_price: Number(p.standard_price || 0),
        gst_rate: Number(p.gst_rate || 0),
      }));
    if (!products.length) return toast.error("Nothing to save");
    setSaving(true);
    try {
      const { data } = await api.post("/products/bulk", { products });
      toast.success(`${data.created_count} product(s) added to the catalogue`);
      if (data.skipped?.length)
        toast.warning(`Skipped: ${data.skipped.map((s) => `${s.name} (${s.reason})`).join(", ")}`);
      setRows([]);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl"
        data-testid="bulk-ingest-dialog">
        <DialogHeader>
          <DialogTitle>Add products from photographs</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 rounded border border-dashed border-border p-4">
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
            hidden onChange={(e) => scan(e.target.files)} data-testid="bulk-ingest-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={scanning}
            data-testid="bulk-ingest-upload-button" className="gap-1.5">
            {scanning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {scanning ? "Reading your photos…" : "Choose photos"}
          </Button>
          <span className="text-xs text-muted-foreground">
            Up to 10 photos at a time. Nothing is saved until you press Save all.
          </span>
        </div>

        {failed.length > 0 && (
          <div className="rounded border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs"
            data-testid="bulk-ingest-failed">
            {failed.map((f) => <div key={f.filename}>{f.filename}: {f.error}</div>)}
          </div>
        )}

        {rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="bulk-ingest-empty">
            <Sparkles className="mx-auto mb-2 h-5 w-5" />
            Snap or upload product photos — the name, category, material, HSN code and GST slab
            are filled in for you to check.
          </p>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div key={r.uid} className="grid gap-3 rounded border border-border p-3 sm:grid-cols-[120px_1fr]"
                data-testid={`bulk-ingest-row-${r.uid}`}>
                <div className="product-image-frame h-[110px]">
                  {r.photos?.[0] && <img src={fileUrl(r.photos[0])} alt={r.name} />}
                </div>
                <div className="grid gap-2 sm:grid-cols-4">
                  <div className="sm:col-span-4 flex items-center gap-2">
                    <Input className="font-medium" value={r.name}
                      data-testid={`bulk-name-${r.uid}`}
                      onChange={(e) => setRow(r.uid, { name: e.target.value })} />
                    <Badge variant={r.confidence >= 0.7 ? "secondary" : "outline"}
                      className="shrink-0 text-[10px]">
                      {Math.round((r.confidence || 0) * 100)}% sure
                    </Badge>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive"
                      data-testid={`bulk-remove-${r.uid}`}
                      onClick={() => setRows((x) => x.filter((y) => y.uid !== r.uid))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Select value={r.category} onValueChange={(v) => setRow(r.uid, { category: v })}>
                    <SelectTrigger data-testid={`bulk-category-${r.uid}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Sub-category" value={r.subcategory || ""}
                    data-testid={`bulk-subcategory-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { subcategory: e.target.value })} />
                  <Input placeholder="Base price ₹ (excl. tax)" value={r.standard_price || ""}
                    data-testid={`bulk-price-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { standard_price: e.target.value })} />
                  <Select value={String(r.gst_rate)}
                    onValueChange={(v) => setRow(r.uid, { gst_rate: Number(v) })}>
                    <SelectTrigger data-testid={`bulk-gst-${r.uid}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {GST_SLABS.map((g) => (
                        <SelectItem key={g} value={String(g)}>
                          {g === 0 ? "0% (nil rated)" : `${g}%`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input placeholder="HSN / SAC" value={r.hsn_sac || ""}
                    data-testid={`bulk-hsn-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { hsn_sac: e.target.value })} />
                  <Input placeholder="Unit" value={r.unit || "Nos"}
                    data-testid={`bulk-unit-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { unit: e.target.value })} />
                  <Input className="sm:col-span-2" placeholder="Material / specifications"
                    value={r.material || ""} data-testid={`bulk-material-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { material: e.target.value })} />
                  <Input className="sm:col-span-4" placeholder="Description"
                    value={r.description || ""} data-testid={`bulk-description-${r.uid}`}
                    onChange={(e) => setRow(r.uid, { description: e.target.value })} />
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={saveAll} disabled={saving || rows.length === 0}
            data-testid="bulk-ingest-save-all">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Save all to catalogue ({rows.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
